import { BaseService } from './baseService'
import { getApiBaseUrl } from '@/config/environment'

export interface LiveAnalysisSession {
  id: number
  project: number | null
  started_at: string
  ended_at: string | null
  status: 'active' | 'completed' | 'failed'
  total_frames: number
  websocket_url?: string
}

export interface LiveAnalysisFrame {
  id: number
  frame_index: number
  timestamp_ms: number
  face_detected: boolean
  emotions: Array<{ emotion: string; confidence: number }>
  dominant_emotion: string | null
  dominant_confidence: number | null
  created_at: string
}

export interface AnalysisResult {
  frame_id: number
  frame_index: number
  timestamp_ms: number
  face_detected: boolean
  /** [x, y, w, h] in the uploaded frame's pixel space, or null when no face. */
  face_area: [number, number, number, number] | null
  emotions: Array<{ emotion: string; confidence: number }>
  dominant_emotion: string | null
  dominant_confidence: number | null
}

export interface SessionSummary {
  total_frames: number
  frames_with_face: number
  dominant_emotions: Record<string, number>
  average_confidence: number
}

export interface SessionResults {
  session: LiveAnalysisSession
  frames: LiveAnalysisFrame[]
  summary: SessionSummary
}

/** Error from the analysis endpoints, carrying the HTTP status and the
 * backend's machine-readable `code` (e.g. 'realtime_disabled', 'busy') so the
 * demo UI can tell a hard outage from a transient one. */
export class AnalysisRequestError extends Error {
  status: number
  code?: string

  constructor(message: string, status: number, code?: string) {
    super(message)
    this.name = 'AnalysisRequestError'
    this.status = status
    this.code = code
  }
}

async function throwResponseError(response: Response): Promise<never> {
  const errorData = await response.json().catch(() => ({}))
  throw new AnalysisRequestError(
    errorData.error || errorData.detail || `HTTP error! status: ${response.status}`,
    response.status,
    errorData.code,
  )
}

class AnalysisService extends BaseService {
  constructor() {
    super(getApiBaseUrl())
  }

  async startLiveAnalysis(projectId?: number): Promise<LiveAnalysisSession> {
    const response = await this.request<LiveAnalysisSession>('/analysis/live/start/', {
      method: 'POST',
      body: JSON.stringify({ project_id: projectId || null }),
    })
    return response
  }

  async sendFrame(
    sessionId: number, 
    frame: Blob, 
    frameIndex: number, 
    timestampMs: number,
    faceBox?: { x: number; y: number; width: number; height: number }
  ): Promise<AnalysisResult> {
    const formData = new FormData()
    formData.append('frame', frame, 'frame.jpg')
    formData.append('frame_index', frameIndex.toString())
    formData.append('timestamp_ms', timestampMs.toString())
    
    // Dodaj współrzędne twarzy jeśli dostępne
    if (faceBox) {
      formData.append('face_x', faceBox.x.toString())
      formData.append('face_y', faceBox.y.toString())
      formData.append('face_width', faceBox.width.toString())
      formData.append('face_height', faceBox.height.toString())
    }

    const token = this.getAuthToken()
    const url = this.urlBuilder.build(`/analysis/live/${sessionId}/frame/`)

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    })

    if (!response.ok) await throwResponseError(response)

    return await response.json()
  }

  async analyzeImage(image: Blob): Promise<{
    emotions: Array<{ emotion: string; confidence: number }>
    dominant_emotion: string | null
    dominant_confidence: number | null
  }> {
    const formData = new FormData()
    formData.append('image', image, 'image.jpg')

    const token = this.getAuthToken()
    const url = this.urlBuilder.build('/analysis/image/analyze/')

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    })

    if (!response.ok) await throwResponseError(response)

    return await response.json()
  }

  async stopLiveAnalysis(sessionId: number): Promise<LiveAnalysisSession> {
    const response = await this.request<LiveAnalysisSession>(`/analysis/live/${sessionId}/stop/`, {
      method: 'POST',
    })
    return response
  }

  async getLiveAnalysisSessions(): Promise<LiveAnalysisSession[]> {
    const response = await this.request<LiveAnalysisSession[]>('/analysis/live/sessions/', {
      method: 'GET',
    })
    return response
  }

  async getLiveAnalysisResults(sessionId: number): Promise<SessionResults> {
    const response = await this.request<SessionResults>(`/analysis/live/${sessionId}/results/`, {
      method: 'GET',
    })
    return response
  }

  connectWebSocket(websocketUrl: string, token: string, onMessage: (result: AnalysisResult) => void): WebSocket {
    const ws = new WebSocket(`${websocketUrl}?token=${token}`)
    
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)
        if (data.type === 'analysis_result' && data.data) {
          onMessage(data.data)
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error)
      }
    }
    
    ws.onerror = (error) => {
      console.error('WebSocket error:', error)
    }
    
    ws.onclose = () => {
      console.log('WebSocket connection closed')
    }
    
    return ws
  }
}

export const analysisService = new AnalysisService()

